import {
  Avatar,
  Box,
  Button,
  Icon,
  Table,
  Text,
  useToggle,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Popover from 'modules/core/components/Popover'
import PopoverContent, { type Option } from 'modules/core/components/Popover/Content'
import createMemo from 'modules/core/hooks/createMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import buildUrl from 'modules/core/lib/buildUrl'
import { convertDate, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useAsyncCan } from 'modules/core/lib/useAsyncCan'
import { getFullDisplayName } from 'modules/core/lib/userDisplayName'
import app from 'modules/core/store/models/app'

import employees from 'modules/employees/store/collections/employees'
import { useReportTracking } from 'modules/reports/hooks/useReportTracking'
import { getDashboardAuthor } from 'modules/reports/lib/getDashboardAuthor'
import favouriteDashboards from 'modules/reports/store/collections/favourite_dashboards'
import type Dashboard from 'modules/reports/store/models/Dashboard'

interface Props {
  dashboard: Dashboard
}

const useMemoCreatedAt = createMemo((createdAt) =>
  createdAt ? convertDate(createdAt, 'backend_datetime', 'ui_long_with_year') : '-'
)

const useMemoUpdateAt = createMemo((updatedAt) =>
  updatedAt ? stringToDate(updatedAt, 'backend_datetime').fromNow() : '-'
)

const onFavourite = async (dashboard: Dashboard, currentUserId: number) => {
  const favourite = favouriteDashboards.find({
    author_id: currentUserId,
    dashboard_id: dashboard.id,
  })

  if (favourite) {
    await asyncAction(() => favourite.destroy(), {
      successFlash: false,
    })
  } else {
    await asyncAction(
      () =>
        favouriteDashboards.create({
          author_id: currentUserId,
          dashboard_id: dashboard.id,
        }),
      {
        successFlash: false,
      }
    )
  }
}

const Actions = ({ dashboard }: Props) => {
  const { navigation } = useNavigation()

  const { track } = useReportTracking()
  const [open, toggle] = useToggle(false)

  const options: Option[] = [
    {
      onClick: () => {
        app.setFlash({
          type: 'correct',
          message: i18n.t('custom_reports.header.download_excel_email'),
        })
        track(trackingEvents.reports.downloadReportSuccess)
        window.location.href = buildUrl(`/reports/dashboards/${dashboard.id}/export`)
      },
      title: i18n.t('common.download_all'),
    },
  ]

  const author = getDashboardAuthor(dashboard)
  const authorEmployee =
    author && !author.isNew
      ? employees.find({
          access_id: author.attributes.id,
        })
      : undefined
  const { value: canManage } = useAsyncCan('reports.manage', authorEmployee || undefined)

  if (canManage && !dashboard.isReadonly) {
    options.unshift({
      to: navigation.reports.dashboards.dashboardId.list.edit.fullPath({
        dashboard_id: dashboard.id,
      }),
      title: i18n.t('common.edit'),
    })
    options.push({
      to: navigation.reports.dashboards.dashboardId.list.delete.fullPath({
        dashboard_id: dashboard.id,
      }),
      title: i18n.t('common.destroy'),
      type: 'wrong',
    })
  }

  return (
    <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
      <Popover
        attachment="top center"
        targetAttachment="bottom center"
        withPreventDefault
        toggle={toggle}
        open={open}
      >
        <Button type="secondary" onClick={toggle(true)} iconName="ellipsis" />
        <PopoverContent onClose={toggle(false)} options={options} />
      </Popover>
      <Button.Router
        to={navigation.reports.dashboards.dashboardId.list.fullPath({ dashboard_id: dashboard.id })}
        type="secondary"
        iconName="arrow-right"
      />
    </Box>
  )
}

const Item = ({ dashboard }: Props) => {
  const { navigation } = useNavigation()
  const { access } = useCurrentSession()
  const createdAt = useMemoCreatedAt(dashboard.attributes.created_at)
  const updatedAt = useMemoUpdateAt(dashboard.attributes.updated_at)
  const favourited = favouriteDashboards.find({
    author_id: access.id,
    dashboard_id: dashboard.id,
  })
  const author = getDashboardAuthor(dashboard)

  return (
    <Table.Row.Router
      to={navigation.reports.dashboards.dashboardId.list.fullPath({ dashboard_id: dashboard.id })}
    >
      <Table.Cell rowClickable={false}>
        <Box
          height="full"
          width="full"
          alignItems="center"
          justifyContent="center"
          onClick={() => onFavourite(dashboard, access.id)}
          cursor="pointer"
        >
          <Icon.Medium
            icon={favourited ? 'star-filled' : 'star'}
            color={favourited ? 'viridian1000' : 'grey400'}
          />
        </Box>
      </Table.Cell>

      <Table.Cell>
        <Text.Normal weight="medium">{dashboard.attributes.title}</Text.Normal>
      </Table.Cell>

      <Table.Cell>
        <Box flexDirection="row" gap="s24">
          <Avatar
            size="s32"
            url={buildUrl(author?.avatar)}
            label={
              author
                ? getFullDisplayName({
                    firstName: author.attributes.first_name,
                    lastName: author.attributes.last_name,
                  })
                : ''
            }
          ></Avatar>
          <Text.Normal weight="medium">
            {author
              ? getFullDisplayName({
                  firstName: author.attributes.first_name,
                  lastName: author.attributes.last_name,
                })
              : '-'}
          </Text.Normal>
        </Box>
      </Table.Cell>

      <Table.Cell>
        <Text.Normal weight="medium">{createdAt}</Text.Normal>
      </Table.Cell>

      <Table.Cell>
        <Text.Normal weight="medium">{updatedAt}</Text.Normal>
      </Table.Cell>

      {/* This cell should not be clickable to avoid nesting of <a> tags */}
      <Table.Cell rowClickable={false} padding="s24">
        <Actions dashboard={dashboard} />
      </Table.Cell>
    </Table.Row.Router>
  )
}

export default observer(Item)
